import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Building2, Briefcase, Handshake, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
type MembershipType = 'individu' | 'organisasi' | 'badan_usaha' | 'mitra';
export function RegisterStep1() {
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState<MembershipType | null>(null);
  const membershipTypes = [{
    id: 'individu' as MembershipType,
    name: 'Anggota Individu',
    description: 'Untuk profesional, pelajar, dan mahasiswa',
    icon: User,
    color: 'text-atios-primary',
    bgColor: 'bg-atios-light',
    features: ['Akses ke semua event ATIOS', 'Networking dengan profesional', 'Diskon khusus member', 'Sertifikat keanggotaan']
  }, {
    id: 'organisasi' as MembershipType,
    name: 'Anggota Organisasi',
    description: 'Untuk komunitas, NGO, dan instansi',
    icon: Building2,
    color: 'text-success',
    bgColor: 'bg-success-light',
    features: ['Representasi organisasi', 'Kolaborasi antar organisasi', 'Akses program khusus', 'Publikasi kegiatan']
  }, {
    id: 'badan_usaha' as MembershipType,
    name: 'Anggota Badan Usaha',
    description: 'Untuk perusahaan dan startup',
    icon: Briefcase,
    color: 'text-warning',
    bgColor: 'bg-warning-light',
    features: ['Posting lowongan kerja', 'Akses talent pool', 'Branding perusahaan', 'Partnership opportunities']
  }, {
    id: 'mitra' as MembershipType,
    name: 'Mitra Asosiasi',
    description: 'Untuk kemitraan strategis',
    icon: Handshake,
    color: 'text-neutral-700',
    bgColor: 'bg-neutral-100',
    features: ['Kemitraan strategis', 'Kolaborasi program', 'Co-branding opportunities', 'Joint events']
  }];
  const handleContinue = () => {
    if (!selectedType) {
      alert('Silakan pilih jenis keanggotaan');
      return;
    }
    // Save to localStorage or context
    localStorage.setItem('registrationData', JSON.stringify({
      membershipType: selectedType
    }));
    navigate('/register/step2');
  };
  return <div className="min-h-screen bg-gradient-to-br from-atios-primary to-blue-700" data-id="element-4497">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-4498">
        {/* Header */}
        <div className="text-center mb-12" data-id="element-4499">
          <h1 className="text-4xl font-bold text-white mb-3" data-id="element-4500">
            Daftar Sebagai Anggota ATIOS
          </h1>
          <p className="text-white/80 text-sm" data-id="element-4501">
            Pilih jenis keanggotaan yang sesuai dengan Anda
          </p>
        </div>

        {/* Progress Bar */}
        <div className="max-w-2xl mx-auto mb-12" data-id="element-4502">
          <div className="flex items-center justify-between mb-2" data-id="element-4503">
            <span className="text-sm font-medium text-white" data-id="element-4504">
              Langkah 1 dari 5
            </span>
            <span className="text-sm text-white/80" data-id="element-4505">20%</span>
          </div>
          <div className="w-full bg-white/20 rounded-full h-2" data-id="element-4506">
            <div className="bg-cyan-400 h-2 rounded-full transition-all" style={{
            width: '20%'
          }} data-id="element-4507" />
          </div>
        </div>

        {/* Membership Type Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12" data-id="element-4508">
          {membershipTypes.map(type => <Card key={type.id} role="button" tabIndex={0} aria-label={`Pilih ${type.name}`} className={`cursor-pointer transition-all hover:shadow-xl ${selectedType === type.id ? 'border-2 border-cyan-400 shadow-lg ring-2 ring-cyan-400/50' : 'border border-white/20 hover:border-cyan-400/50'}`} onClick={() => setSelectedType(type.id)} onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setSelectedType(type.id);
          }
        }} data-id="element-4509">
              <CardContent className="p-6" data-id="element-4510">
                <div className={`p-4 rounded-lg ${type.bgColor} mb-4 inline-block`} data-id="element-4511">
                  <type.icon className={`w-8 h-8 ${type.color}`} data-id="element-4512" />
                </div>
                <h3 className="text-xl font-semibold text-neutral-900 mb-2" data-id="element-4513">
                  {type.name}
                </h3>
                <p className="text-sm text-neutral-600 mb-4" data-id="element-4514">
                  {type.description}
                </p>
                <ul className="space-y-2" data-id="element-4515">
                  {type.features.map((feature, i) => <li key={i} className="flex items-start gap-2 text-sm text-neutral-700" data-id="element-4516">
                      <span className="text-cyan-500 mt-0.5" data-id="element-4517">✓</span>
                      <span data-id="element-4518">{feature}</span>
                    </li>)}
                </ul>
                {selectedType === type.id && <div className="mt-4 pt-4 border-t border-neutral-200" data-id="element-4519">
                    <span className="text-sm font-medium text-cyan-600" data-id="element-4520">
                      ✓ Dipilih
                    </span>
                  </div>}
              </CardContent>
            </Card>)}
        </div>

        {/* Action Buttons */}
        <div className="max-w-2xl mx-auto" data-id="element-4521">
          <Button onClick={handleContinue} disabled={!selectedType} className="w-full bg-cyan-500 hover:bg-cyan-600 text-white" size="lg" data-id="element-4522">
            Lanjutkan
            <ChevronRight className="w-5 h-5 ml-2" data-id="element-4523" />
          </Button>
          <div className="text-center mt-4" data-id="element-4524">
            <button onClick={() => navigate('/login')} className="text-white/80 hover:text-white text-sm underline" data-id="element-4525">
              Sudah punya akun? Login di sini
            </button>
          </div>
        </div>
      </div>
    </div>;
}